import { CapType } from "types/enums";

/**
 * A `CapType` is really two independent choices packed into one BE tag: a period ("day", "all", ...) and,
 * for an offer, a conversion type ("click" | "lead"). The editor shows them as two selects instead of one
 * flat dropdown — this file only converts between the two shapes, `CapType` itself is unchanged.
 */
export type CapPeriod = "hour" | "day" | "week" | "month" | "year" | "all" | "minute";
export type CapKind = "click" | "lead";

/** Display order requested for the period select. `minute` is a valid BE period but not part of it — a
 * rule loaded with `cap_*_minute` must still stay editable, so `getCapPeriodOptions` adds it back when
 * it's the currently selected value, even though it's never offered on a new rule. */
export const CAP_PERIOD_ORDER: readonly CapPeriod[] = ["hour", "day", "week", "month", "year", "all"];

export const CAP_KIND_ORDER: readonly CapKind[] = ["click", "lead"];

const CAP_TAG_PATTERN = /^cap_(?:(click|lead)_)?([a-z]+)$/;

export interface CapTagParts {
    period: CapPeriod;
    kind: CapKind | null;
}

export const parseCapTag = (tag: CapType): CapTagParts => {
    const match = CAP_TAG_PATTERN.exec(tag);
    if (!match) {
        return { period: "day", kind: null };
    }
    return { period: match[2] as CapPeriod, kind: (match[1] as CapKind | undefined) ?? null };
};

/** Rebuilds the BE tag from a period + kind pair. Falls back to the original tag if the combination
 * doesn't exist (e.g. cascade has no "minute" + kind variant). */
export const buildCapTag = (period: CapPeriod, kind: CapKind | null, fallback: CapType): CapType => {
    const candidate = kind ? `cap_${kind}_${period}` : `cap_${period}`;
    return (Object.values(CapType) as string[]).includes(candidate) ? (candidate as CapType) : fallback;
};

/** Periods offered by the given tags, in `CAP_PERIOD_ORDER`, plus the currently selected one if it's
 * otherwise missing (a loaded filter must stay editable even for an option no longer offered). */
export const getCapPeriodOptions = (tags: readonly CapType[], current: CapPeriod): CapPeriod[] => {
    const available = new Set(tags.map((tag) => parseCapTag(tag).period));
    const ordered = CAP_PERIOD_ORDER.filter((period) => available.has(period));
    return ordered.includes(current) ? ordered : [current, ...ordered];
};

export const hasCapKind = (tags: readonly CapType[]): boolean => tags.some((tag) => parseCapTag(tag).kind !== null);
